import type { ICharacter, IConversationMessage, MediaFileBlurVerdicts } from "@next-platform/client-common-core";

import chooseCharacterFromMessage from "./chooseCharacterFromMessage";
import getIsChatImageBlurred from "./getIsChatImageBlurred";

type GetChatImageBlurVerdictsOptions = {
  /** The messages whose images the chat shows, in the order it shows them. */
  readonly messages: readonly IConversationMessage[];
  /** The chat's character; in a group chat, the one whose sub-characters the messages speak as. */
  readonly character: ICharacter | null | undefined;
  readonly blurNSFW: boolean | undefined;
  readonly isAuthenticated: boolean | undefined;
};

/**
 * The blur verdict of every gallery file the chat shows, resolved the way each bubble resolves its own. The
 * details modal reads these so a file opens, and is stepped to, looking the way its bubble did: a group chat's
 * images span characters, and one verdict per file is what keeps a clear character's `false` off a blurred
 * character's image. An older message names no file and is skipped; a file shown twice keeps its first verdict.
 */
export default function getChatImageBlurVerdicts({
  messages,
  character,
  blurNSFW,
  isAuthenticated,
}: GetChatImageBlurVerdictsOptions): MediaFileBlurVerdicts {
  if (!character) {
    return {};
  }
  const verdicts: MediaFileBlurVerdicts = {};
  messages.forEach((message) => {
    const fileId = message.media_file_id;
    if (!fileId || verdicts[fileId] !== undefined) {
      return;
    }
    const messageCharacter = chooseCharacterFromMessage({ message, character });
    verdicts[fileId] = getIsChatImageBlurred({ character: messageCharacter, message, blurNSFW, isAuthenticated });
  });
  return verdicts;
}
